import { Bolt, DirectionsRun, MonitorHeart, PersonOff, Shield, Thunderstorm, WarningAmber } from "@mui/icons-material";
import { Box } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import { ResponseTile } from "~ui/molecules/ResponseTile/ResponseTile";

export const srpAvatarBg = {
  evacuate: "#1b5e20",
  hold: "#5e35b1",
  lockdown: "#b71c1c",
  secure: "#1565c0",
  shelter: "#e65100",
} as const;

/** I Love U Guys SRP assets — https://iloveuguys.org/The-Standard-Response-Protocol.html */
export const srpImage = {
  evacuate: "https://iloveuguys.org/logosandicons/evacuate.gif",
  hold: "https://iloveuguys.org/logosandicons/hold.gif",
  lockdown: "https://iloveuguys.org/logosandicons/lockdown.gif",
  secure: "https://iloveuguys.org/logosandicons/secure.gif",
  shelter: "https://iloveuguys.org/logosandicons/shelter.gif",
} as const;

/** Distinct card backgrounds per SRP action (different from each other and from generic critical/alert tiles). */
export const srpTileSx = {
  evacuate: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #66bb6a 0%, #2e7d32 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #43a047 0%, #1b5e20 100%)",
    color: "common.white",
  },
  hold: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #9575cd 0%, #5e35b1 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #7e57c2 0%, #4527a0 100%)",
    color: "common.white",
  },
  lockdown: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #ef5350 0%, #c62828 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #e53935 0%, #b71c1c 100%)",
    color: "common.white",
  },
  secure: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #42a5f5 0%, #1565c0 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #1e88e5 0%, #0d47a1 100%)",
    color: "common.white",
  },
  shelter: {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #ffb74d 0%, #e65100 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #fb8c00 0%, #bf360c 100%)",
    color: "common.white",
  },
} as const satisfies Record<keyof typeof srpImage, SxProps<Theme>>;

export type CustomTileVariant = "interactive" | "disabled-drill" | "omit";

export interface EmergencyDispatchTileGridProps {
  disabled: boolean;
  /** Receives action ids matching `DISPATCH_EMERGENCY_KEYS` or `"custom"` when the custom tile is interactive. */
  onPress: (actionId: string) => void;
  /**
   * `interactive` — full visitor-management behavior (custom shows snackbar).
   * `disabled-drill` — same 6×6 layout as live; Custom is non-actionable (drills only use standard dispatch keys).
   * `omit` — hide Custom (uneven columns; rarely needed).
   */
  customTileVariant?: CustomTileVariant;
}

/**
 * Two-column emergency action grid (same layout as visitor quick response).
 */
export function EmergencyDispatchTileGrid({
  disabled,
  onPress,
  customTileVariant = "interactive",
}: EmergencyDispatchTileGridProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 1 auto",
        flexDirection: { md: "row", xs: "column" },
        gap: 1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flex: { md: "1 1 0", xs: "0 0 auto" },
          flexDirection: "column",
          gap: 1,
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <ResponseTile
          disabled={disabled}
          icon={<WarningAmber />}
          kind="critical"
          label="Active Shooter"
          sx={{ flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("active-shooter")}
        />
        <ResponseTile
          avatarBg={srpAvatarBg.hold}
          disabled={disabled}
          imageAlt="SRP Hold"
          imageSrc={srpImage.hold}
          kind="alert"
          label="SRP — Hold"
          sx={{ ...srpTileSx.hold, flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("srp-hold")}
        />
        <ResponseTile
          avatarBg={srpAvatarBg.secure}
          disabled={disabled}
          imageAlt="SRP Secure"
          imageSrc={srpImage.secure}
          kind="alert"
          label="SRP — Secure"
          sx={{ ...srpTileSx.secure, flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("srp-secure")}
        />
        <ResponseTile
          avatarBg={srpAvatarBg.lockdown}
          disabled={disabled}
          imageAlt="SRP Lockdown"
          imageSrc={srpImage.lockdown}
          kind="alert"
          label="SRP — Lockdown"
          sx={{ ...srpTileSx.lockdown, flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("srp-lockdown")}
        />
        <ResponseTile
          avatarBg={srpAvatarBg.evacuate}
          disabled={disabled}
          imageAlt="SRP Evacuate"
          imageSrc={srpImage.evacuate}
          kind="alert"
          label="SRP — Evacuate"
          sx={{ ...srpTileSx.evacuate, flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("srp-evacuate")}
        />
        <ResponseTile
          avatarBg={srpAvatarBg.shelter}
          disabled={disabled}
          imageAlt="SRP Shelter"
          imageSrc={srpImage.shelter}
          kind="alert"
          label="SRP — Shelter"
          sx={{ ...srpTileSx.shelter, flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("srp-shelter")}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: { md: "1 1 0", xs: "0 0 auto" },
          flexDirection: "column",
          gap: 1,
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <ResponseTile
          disabled={disabled}
          icon={<DirectionsRun />}
          kind="critical"
          label="Emergency Evacuation"
          sx={{ flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("emergency-evacuation")}
        />
        <ResponseTile
          disabled={disabled}
          icon={<MonitorHeart />}
          kind="alert"
          label="Medical"
          sx={{ flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("medical")}
        />
        <ResponseTile
          disabled={disabled}
          icon={<Thunderstorm />}
          kind="alert"
          label="Weather"
          sx={{ flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("weather")}
        />
        <ResponseTile
          disabled={disabled}
          icon={<PersonOff />}
          kind="alert"
          label="Stranger"
          sx={{ flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("stranger")}
        />
        <ResponseTile
          disabled={disabled}
          icon={<Bolt />}
          kind="alert"
          label="Fight"
          sx={{ flex: { md: "1 1 0", xs: "0 0 auto" } }}
          onPress={() => onPress("fight")}
        />
        {customTileVariant === "omit" ? null : customTileVariant === "disabled-drill" ? (
          <ResponseTile
            disabled
            icon={<Shield />}
            kind="alert"
            label="Custom"
            sx={{ flex: { md: "1 1 0", xs: "0 0 auto" } }}
            title="Custom emergencies are not available as drills. Use a standard action or configure under Emergency Management."
            onPress={() => {}}
          />
        ) : (
          <ResponseTile
            disabled={disabled}
            icon={<Shield />}
            kind="alert"
            label="Custom"
            sx={{ flex: { md: "1 1 0", xs: "0 0 auto" } }}
            onPress={() => onPress("custom")}
          />
        )}
      </Box>
    </Box>
  );
}
